import React, { useEffect, useState, useRef, useCallback } from "react";
import { createBooking } from "../lib/api";
import { X, Check } from "lucide-react";

const SERVICES = [
  "Smile Makeover", "Teeth Whitening", "Veneers & Laminates", "Aligners / Invisalign",
  "Dental Implants", "Root Canal Treatment", "Tooth-Coloured Fillings", "Crowns & Bridges",
  "Check-up & Cleaning", "Not Sure — Recommend Me",
];

export default function BookingModal({ open, onClose, initialService }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "Smile Makeover", preferred_date: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const modalRef = useRef(null);
  const previousFocus = useRef(null);

  useEffect(() => {
    if (!open) { setDone(false); setErr(""); }
    if (open && initialService) setForm((f) => ({ ...f, service: initialService }));
    document.documentElement.style.overflow = open ? "hidden" : "";

    // WCAG: Focus management
    if (open) {
      previousFocus.current = document.activeElement;
      setTimeout(() => modalRef.current?.focus(), 50);
    } else if (previousFocus.current && typeof previousFocus.current.focus === "function") {
      previousFocus.current.focus();
    }

    return () => { document.documentElement.style.overflow = ""; };
  }, [open, initialService]);

  // WCAG: Focus trap
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key !== "Tab") return;

    const focusable = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      last.focus(); e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus(); e.preventDefault();
    }
  }, [onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await createBooking(form);
      setDone(true);
      setForm({ name: "", phone: "", email: "", service: "Smile Makeover", preferred_date: "", message: "" });
    } catch (e2) {
      setErr("Something went wrong. Please WhatsApp us at +1 (555) 123-4567.");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-modal-title"
      data-testid="booking-modal"
      onKeyDown={handleKeyDown}
    >
      <div className="absolute inset-0 bg-sage-900/45 backdrop-blur-md" onClick={onClose} aria-hidden="true" />
      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative w-full max-w-2xl bg-cream rounded-[28px] p-7 md:p-10 ring-gold-soft outline-none"
      >
        <button
          onClick={onClose}
          data-testid="booking-close"
          className="absolute top-5 right-5 w-10 h-10 grid place-items-center rounded-full bg-white text-sage-900 border border-sage-700/10"
          aria-label="Close booking form"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>

        {!done ? (
          <>
            <div className="text-[11px] uppercase tracking-[0.32em] text-sage-700/80 mb-3">Begin — Consultation</div>
            <h3 id="booking-modal-title" className="font-display text-4xl md:text-5xl leading-[1.02] tracking-tightest">
              Let's design your <span className="font-script text-gold-500">smile</span>.
            </h3>
            <p className="mt-3 text-sm text-ink/65 max-w-md">
              Share a few details — our team will reach out within a few hours to confirm.
            </p>

            <form onSubmit={submit} className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Your name" testid="bk-name" htmlFor="booking-name">
                <input
                  required minLength={2} maxLength={80} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input" data-testid="booking-name" id="booking-name"
                  autoComplete="name"
                />
              </Field>
              <Field label="Phone" testid="bk-phone" htmlFor="booking-phone">
                <input
                  required type="tel" minLength={6} value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input" data-testid="booking-phone" id="booking-phone"
                  autoComplete="tel"
                />
              </Field>
              <Field label="Email (optional)" testid="bk-email" htmlFor="booking-email">
                <input
                  type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input" data-testid="booking-email" id="booking-email"
                  autoComplete="email"
                />
              </Field>
              <Field label="Preferred date" testid="bk-date" htmlFor="booking-date">
                <input
                  type="date" value={form.preferred_date}
                  onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                  className="input" data-testid="booking-date" id="booking-date"
                />
              </Field>
              <Field label="Service of interest" testid="bk-service" full htmlFor="booking-service">
                <select
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                  className="input"
                  data-testid="booking-service" id="booking-service"
                >
                  {SERVICES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Anything we should know? (optional)" testid="bk-message" full htmlFor="booking-message">
                <textarea
                  rows={3} maxLength={500} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="input resize-none" data-testid="booking-message" id="booking-message"
                />
              </Field>

              {err && (
                <div className="md:col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" role="alert" data-testid="booking-error">{err}</div>
              )}

              <div className="md:col-span-2 mt-2 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  data-cursor="hover"
                  className="flex-1 rounded-full bg-sage-800 text-cream py-3.5 uppercase tracking-[0.24em] text-xs btn-glow disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Confirm consultation"}
                </button>
                <a
                  href="https://wa.me/15551234567?text=Hi%20Dentist%2C%20I%27d%20like%20to%20book%20a%20consultation."
                  target="_blank" rel="noreferrer"
                  data-testid="booking-whatsapp"
                  data-cursor="hover"
                  className="flex-1 rounded-full border border-sage-800/30 text-sage-900 py-3.5 uppercase tracking-[0.24em] text-xs text-center"
                >
                  WhatsApp instead
                </a>
              </div>
            </form>
            <style>{`
              .input { width: 100%; background: #fff; border: 1px solid rgba(56,79,49,0.15); border-radius: 16px; padding: 0.9rem 1rem; font-size: 0.92rem; color: #1a1d18; outline: none; transition: all .25s ease; }
              .input:focus { border-color: rgba(201,169,110,0.7); box-shadow: 0 0 0 4px rgba(201,169,110,0.18); }
            `}</style>
          </>
        ) : (
          <div className="text-center py-8" data-testid="booking-success" role="status" aria-live="polite">
            <div className="mx-auto w-16 h-16 rounded-full bg-sage-700 text-cream grid place-items-center mb-5" aria-hidden="true">
              <Check className="w-7 h-7" />
            </div>
            <h3 className="font-display text-4xl tracking-tightest">You're on the list.</h3>
            <p className="mt-3 text-ink/70 max-w-md mx-auto">
              We've received your request. Our team will reach out shortly to confirm your slot. We can't wait to see your smile.
            </p>
            <button
              onClick={onClose}
              className="mt-7 rounded-full bg-sage-800 text-cream px-7 py-3 uppercase tracking-[0.24em] text-xs"
              data-testid="booking-success-close"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, full, testid, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className={`block ${full ? "md:col-span-2" : ""}`} data-testid={testid}>
      <span className="block text-[10px] uppercase tracking-[0.28em] text-sage-700/80 mb-2">{label}</span>
      {children}
    </label>
  );
}
